/**
 * PHASE 4 — compass/heading calibration.
 *
 * Absolute orientation data can be wrong for many reasons (magnetometer
 * calibration, local field distortions, platform differences in reference
 * north, magnetic declination). The calibration stores a single yaw offset,
 * applied in the DEVICE frame, that corrects heading errors:
 *
 *   v_earth = rotateVector(qCorrected, v_device)
 *   qCorrected = q_deviceToEarth * Rz(yawOffset)
 *
 * The user experience is "point at a known object, tell the app which one";
 * the app computes the offset from the known azimuth. All math here is pure;
 * persistence is injectable for tests.
 */

import type { Quat } from "./quaternion";
import { mul, quat } from "./quaternion";

const STORAGE_KEY = "starlight-sky:calibration";

export interface CalibrationState {
  /** Yaw offset in radians, applied about the device z axis. */
  yawOffsetRad: number;
  updatedAtMs: number;
  /** How the offset was obtained. */
  appliedBy: "auto" | "manual";
}

export function applyCalibration(qDeviceToEarth: Quat, offsetRad: number): Quat {
  // Right-multiply: the device is yawed by offsetRad about its own z axis.
  // With positive offset the device top turns counterclockwise when viewed
  // from the front (toward screen-right), which fixes a misreported heading
  // pointing the wrong way. Direction pinned by tests.
  return mul(qDeviceToEarth, quat(0, 0, Math.sin(offsetRad / 2), Math.cos(offsetRad / 2)));
}

export function removeCalibration(qCorrected: Quat, offsetRad: number): Quat {
  return applyCalibration(qCorrected, -offsetRad);
}

/**
 * Compute the calibration offset that takes `measured` device-to-earth and,
 * after adding the offset, would align with `expected`:
 *
 *   expected ~ applyCalibration(measured, offset)
 *
 * The error rotation qErr = conj(measured) * expected is expressed in the
 * earth frame; its rotation about the world-Z (up) axis is exactly the
 * heading mismatch. We extract that via the axis-angle representation. When
 * measured and expected share the same pitch/roll, the result is exact;
 * with small pose differences it is the least-squares yaw estimate.
 *
 * Returns the offset in radians.
 */
export function computeYawOffset(measured: Quat, expected: Quat): number {
  const qErr = mul({ x: -measured.x, y: -measured.y, z: -measured.z, w: measured.w }, expected);
  // Axis-angle decomposition (w >= 0 by construction for the angles at play).
  const w = Math.min(1, Math.max(-1, qErr.w));
  const angle = 2 * Math.acos(w);
  const halfSin = Math.sqrt(Math.max(0, 1 - w * w));
  if (halfSin < 1e-12) return 0; // identity: no heading error
  const axisZ = qErr.z / halfSin;
  // Project the error rotation onto the world-Z axis (signed heading).
  return angle * axisZ;
}

function normalizedAngle(a: number): number {
  let r = a % (2 * Math.PI);
  if (r > Math.PI) r -= 2 * Math.PI;
  if (r < -Math.PI) r += 2 * Math.PI;
  return r;
}

export class CalibrationStore {
  private state: CalibrationState;
  private storage: Pick<Storage, "getItem" | "setItem"> | null;

  constructor(
    storage: Pick<Storage, "getItem" | "setItem"> | null = null,
    initial: CalibrationState = { yawOffsetRad: 0, updatedAtMs: 0, appliedBy: "manual" },
  ) {
    this.storage = storage;
    this.state = this.restore() ?? { ...initial };
  }

  private restore(): CalibrationState | null {
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<CalibrationState>;
      if (typeof parsed.yawOffsetRad === "number" && Number.isFinite(parsed.yawOffsetRad)) {
        return {
          yawOffsetRad: parsed.yawOffsetRad,
          updatedAtMs: typeof parsed.updatedAtMs === "number" ? parsed.updatedAtMs : 0,
          appliedBy: parsed.appliedBy === "auto" ? "auto" : "manual",
        };
      }
    } catch {
      /* corrupted */
    }
    return null;
  }

  private persist() {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      /* non-fatal */
    }
  }

  get(): CalibrationState {
    return this.state;
  }

  getOffsetRad(): number {
    return this.state.yawOffsetRad;
  }

  /** Apply an offset (degrees), clamping to [-180, 180]. */
  setOffsetDeg(offsetDeg: number, appliedBy: "auto" | "manual" = "manual"): CalibrationState {
    this.state = {
      yawOffsetRad: normalizedAngle((offsetDeg * Math.PI) / 180),
      updatedAtMs: Date.now(),
      appliedBy,
    };
    this.persist();
    return this.state;
  }

  clear(): CalibrationState {
    this.state = { yawOffsetRad: 0, updatedAtMs: Date.now(), appliedBy: "manual" };
    this.persist();
    return this.state;
  }

  /** Calibrate using a measured and an expected device-to-earth quaternion. */
  calibrateFromPair(measured: Quat, expected: Quat): CalibrationState {
    const offsetRad = computeYawOffset(measured, expected);
    return this.setOffsetDeg((offsetRad * 180) / Math.PI, "auto");
  }
}